import { useEffect, useState } from "react";
import { Tag, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money } from "@/components/Billing";

/** Live price breakdown with a promo / discount code field. Reports {code, total} upward. */
export const PriceQuote = ({ sqft, onChange, compact = false }) => {
  const [input, setInput] = useState("");
  const [code, setCode] = useState("");
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!sqft || sqft < 1) { setQ(null); onChange?.({ code: "", total: 0 }); return; }
    let alive = true;
    const t = setTimeout(() => api.get("/billing/quote", { params: { sqft, code } }).then(({ data }) => {
      if (!alive) return;
      setQ(data);
      setErr(data.code_error || "");
      onChange?.({ code: data.code || "", total: data.total, sqft: data.sqft });
    }).catch((e) => setErr(errMsg(e))), 250);
    return () => { alive = false; clearTimeout(t); };
  }, [sqft, code]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (e) => { e?.preventDefault(); setCode(input.trim().toUpperCase()); };
  const clear = () => { setInput(""); setCode(""); };
  const discounted = q && q.total < q.base;

  return (
    <div className={`space-y-2 ${compact ? "text-xs" : "text-sm"}`} data-testid="price-quote">
      <div className="flex gap-2">
        <div className="flex-1 min-w-0 flex items-center gap-2 bg-[var(--card)] border border-[var(--line)] focus-within:border-[var(--cyan)] px-3 transition-colors">
          <Tag size={13} className="text-[var(--muted)] shrink-0" />
          <input data-testid="promo-code-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply(e)}
            placeholder="Promo code" className="flex-1 min-w-0 bg-transparent outline-none py-2 font-mono uppercase text-xs" />
          {code && <button type="button" data-testid="promo-code-clear" onClick={clear} className="text-[var(--muted)] hover:text-white"><X size={13} /></button>}
        </div>
        <button type="button" data-testid="promo-code-apply" onClick={apply} disabled={!input.trim()} className="px-3 text-xs border border-[var(--line)] hover:border-[var(--cyan)] disabled:opacity-40 transition-colors">Apply</button>
      </div>
      {err && <p className="text-xs text-red-400" data-testid="promo-code-error">{err}</p>}
      {!err && q?.code_note && <p className="text-xs text-[var(--amber)]" data-testid="promo-code-note">{q.code_note}</p>}
      {q && (
        <div className="font-mono text-[11px] text-[var(--muted)] space-y-0.5" data-testid="price-breakdown">
          <p className="flex justify-between"><span>{q.sqft.toLocaleString()} sq ft × $1</span><span>{money(q.base)}</span></p>
          {q.client_discount > 0 && <p className="flex justify-between text-[var(--emerald)]" data-testid="client-discount-line"><span>Your discount ({q.client_discount_pct}%)</span><span>−{money(q.client_discount)}</span></p>}
          {q.code_discount > 0 && <p className="flex justify-between text-[var(--emerald)]" data-testid="code-discount-line"><span>Code {q.code}{q.code_label ? ` · ${q.code_label}` : ""}</span><span>−{money(q.code_discount)}</span></p>}
          <p className={`flex justify-between pt-1 border-t border-[var(--line)] ${discounted ? "text-[var(--amber)]" : "text-white"}`}><span>Total</span><span data-testid="quote-total">{money(q.total)}</span></p>
        </div>
      )}
    </div>
  );
};
